import { useEffect, useState } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import type Graphic from '@arcgis/core/Graphic';
import { getAppConfig, getAttributeServiceUrl } from '../../../utils/ReduxHelper';
import FieldInfo from '../FieldInfo.json';
import { useNavigate } from 'react-router';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Link } from 'react-router-dom';
import { Avatar, Chip } from '@mui/material';
import ColorConstants from '../../../defaults/ColorConstants';
import AddIcon from '@mui/icons-material/Add';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import RemoveDoneIcon from '@mui/icons-material/RemoveDone';
import FieldNames from '../../../defaults/FieldNames.json';
import { isFeaturePostFire } from '../../../utils/FeatureHelper';
import { loadRelatedActualTreatments } from '../../../utils/MapUtilities';
import { formatDateForDisplay } from '../../../utils/DateHelper';

interface ProgressiveReportingListProps {
    joinId: number;
    joinFeature: string;
    isApprovedAndFunded: boolean;
    onAddActualClicked: () => void;
    onFinalizeClicked: () => void;
    onUnfinalizeClicked: () => void;
    status: string;
}

export default function ProgressiveReportingList({
    joinId,
    joinFeature,
    isApprovedAndFunded,
    status,
    onAddActualClicked,
    onFinalizeClicked,
    onUnfinalizeClicked,
}: ProgressiveReportingListProps) {
    const navigate = useNavigate();

    const config: any = getAppConfig();

    const rootServiceUrl = getAttributeServiceUrl();
    const [actualFeatures, setActualFeatures] = useState<Graphic[]>([]);
    const [canAdd, setCanAdd] = useState<boolean>(false);
    const [canFinalize, setCanFinalize] = useState<boolean>(false);

    // Null check for config
    if (!config) return null;
    const url = `${rootServiceUrl}/${config.actionAttributes[config.env].layerId}`;

    useEffect(() => {
        if (!joinId || !joinFeature) return;
        loadRelatedActualTreatments(url, joinId, joinFeature).then((features: Graphic[]) =>
            setActualFeatures(features)
        );
    }, [url, joinId, joinFeature]);

    useEffect(() => {
        const filteredActuals = actualFeatures.filter(
            (f) => !f.attributes[FieldInfo.CompletionDate.name]
        );
        if (actualFeatures.length > 30) {
            setCanAdd(false);
        } else if (filteredActuals.length === 0) {
            setCanAdd(true);
        } else {
            setCanAdd(false);
        }

        if (actualFeatures.length > 0 && filteredActuals.length === 0) {
            setCanFinalize(true);
        } else {
            setCanFinalize(false);
        }
    }, [actualFeatures]);

    return (
        <div>
            <div>
                <Chip
                    disabled={
                        !isApprovedAndFunded || !canAdd || FieldNames.statuses.completed === status
                    }
                    className='required-field-status-chip'
                    onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        onAddActualClicked();
                    }}
                    style={{
                        backgroundColor: `${ColorConstants.AvatarBackgroundColor}`,
                        marginBottom: 5,
                        marginTop: 5,
                        zIndex: 5,
                        marginLeft: 10,
                    }}
                    avatar={
                        <Avatar
                            style={{
                                fontSize: '12px',
                                width: '22px',
                                height: '22px',
                                backgroundColor: ColorConstants.AvatarColor,
                                color: ColorConstants.WhiteAlien,
                            }}
                        >
                            <AddIcon />
                        </Avatar>
                    }
                    label={<span style={{ fontSize: '12px', lineHeight: '21px' }}>Add Actual</span>}
                />
                {FieldNames.statuses.completed !== status ? (
                    <Chip
                        disabled={!canFinalize}
                        className='required-field-status-chip'
                        onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            onFinalizeClicked();
                        }}
                        style={{
                            backgroundColor: `${ColorConstants.AvatarBackgroundColor}`,
                            marginBottom: 5,
                            marginTop: 5,
                            zIndex: 5,
                            marginLeft: 10,
                        }}
                        avatar={
                            <Avatar
                                style={{
                                    fontSize: '12px',
                                    width: '22px',
                                    height: '22px',
                                    backgroundColor: ColorConstants.AvatarColor,
                                    color: ColorConstants.WhiteAlien,
                                }}
                            >
                                <DoneAllIcon />
                            </Avatar>
                        }
                        label={
                            <span style={{ fontSize: '12px', lineHeight: '21px' }}>Finalize</span>
                        }
                    />
                ) : (
                    <Chip
                        disabled={false}
                        className='required-field-status-chip'
                        onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            onUnfinalizeClicked();
                        }}
                        style={{
                            backgroundColor: `${ColorConstants.AvatarBackgroundColor}`,
                            marginBottom: 5,
                            marginTop: 5,
                            zIndex: 5,
                            marginLeft: 10,
                        }}
                        avatar={
                            <Avatar
                                style={{
                                    fontSize: '12px',
                                    width: '22px',
                                    height: '22px',
                                    backgroundColor: ColorConstants.AvatarColor,
                                    color: ColorConstants.WhiteAlien,
                                }}
                            >
                                <RemoveDoneIcon />
                            </Avatar>
                        }
                        label={
                            <span style={{ fontSize: '12px', lineHeight: '21px' }}>
                                Un-Finalize
                            </span>
                        }
                    />
                )}
            </div>
            {actualFeatures.length === 0 ? null : (
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>{FieldInfo.Name.alias}</TableCell>
                                <TableCell>{FieldInfo.LocalID.alias}</TableCell>
                                <TableCell>{FieldInfo.CalculatedArea.alias}</TableCell>
                                <TableCell>{FieldInfo.InitiationDate.alias}</TableCell>
                                <TableCell>{FieldInfo.CompletionDate.alias}</TableCell>
                                <TableCell /> {/* Arrow icon column header */}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {actualFeatures.map((row, idx) => (
                                <TableRow
                                    key={idx}
                                    hover
                                    style={{ cursor: 'pointer' }}
                                    tabIndex={0}
                                    role='button'
                                    onClick={() =>
                                        navigate(
                                            `/${isFeaturePostFire(row.attributes) ? 'pf' : 'f'}/treatments/${row.attributes.OBJECTID}`
                                        )
                                    }
                                >
                                    <TableCell>{row.attributes[FieldInfo.Name.name]}</TableCell>
                                    <TableCell>{row.attributes[FieldInfo.LocalID.name]}</TableCell>
                                    <TableCell>
                                        {row.attributes[FieldInfo.CalculatedArea.name]}{' '}
                                        {row.attributes[FieldInfo.AreaUnit.name]}
                                    </TableCell>
                                    <TableCell>
                                        {row.attributes[FieldInfo.InitiationDate.name] &&
                                            formatDateForDisplay(
                                                row.attributes[FieldInfo.InitiationDate.name]
                                            )}
                                    </TableCell>
                                    <TableCell>
                                        {row.attributes[FieldInfo.CompletionDate.name] &&
                                            formatDateForDisplay(
                                                row.attributes[FieldInfo.CompletionDate.name]
                                            )}
                                    </TableCell>
                                    <TableCell align='right'>
                                        <Link
                                            to={`/${isFeaturePostFire(row.attributes) ? 'pf' : 'f'}/treatments/${row.attributes.OBJECTID}`}
                                            target={'_blank'}
                                            style={{ textDecoration: 'none' }}
                                        >
                                            <OpenInNewIcon style={{ fill: 'unset' }} />
                                        </Link>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </div>
    );
}
